// src/components/modals/CourseImagePickerModal.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const PAGE_SIZE = 10;

const CourseImagePickerModal = ({
  isOpen,
  onClose,
  onSubmit,
  courseTitle = "קורס",
  initialImage = "",
  collections = [],
}) => {
  const modalRef = useRef(null);

  const [activeCategory, setActiveCategory] = useState(collections[0]?.key || "");
  const [selectedImage,  setSelectedImage]  = useState(initialImage || "");
  const [hoveredImage,   setHoveredImage]   = useState(null);
  const [page,           setPage]           = useState(1);

  /* scroll lock + ESC */
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [isOpen, onClose]);

  useEffect(() => { setSelectedImage(initialImage || ""); }, [initialImage]);

  useEffect(() => {
    if (!collections.length) { setActiveCategory(""); return; }
    if (!collections.some(c => c.key === activeCategory)) setActiveCategory(collections[0].key);
  }, [collections, activeCategory]);

  const activeImages = useMemo(() => {
    const cat = collections.find(c => c.key === activeCategory);
    return (cat?.images || []).filter(x => typeof x === "string" && x.trim());
  }, [collections, activeCategory]);

  useEffect(() => { setPage(1); }, [activeCategory, isOpen]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(activeImages.length / PAGE_SIZE)), [activeImages.length]);
  useEffect(() => { setPage(p => Math.min(Math.max(1, p), totalPages)); }, [totalPages]);

  const pageImages = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return activeImages.slice(start, start + PAGE_SIZE);
  }, [activeImages, page]);

  const pageNumbers = useMemo(() => {
    const items = [];
    for (let p = 1; p <= totalPages; p++) {
      const show = p === 1 || p === totalPages || Math.abs(p - page) <= 1;
      if (p === page - 2 && p > 2)              items.push({ type: "ellipsis", key: `ea${p}` });
      else if (p === page + 2 && p < totalPages - 1) items.push({ type: "ellipsis", key: `eb${p}` });
      else if (show)                              items.push({ type: "page", value: p, key: p });
    }
    return items;
  }, [totalPages, page]);

  const handleSave = () => { if (!selectedImage) return; onSubmit?.(selectedImage); };
  const goPrev = () => setPage(p => Math.max(1, p - 1));
  const goNext = () => setPage(p => Math.min(totalPages, p + 1));

  if (!isOpen) return null;

  /* ── shared pagination button cls ── */
  const pageBtnCls = (disabled) =>
    `w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-semibold transition-all duration-150
     ${disabled
       ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
       : "bg-white text-gray-500 border-gray-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-300 shadow-sm active:scale-95"}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      dir="rtl"
      onMouseDown={(e) => { if (!modalRef.current?.contains(e.target)) onClose?.(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" />

      {/* Modal */}
      <div
        ref={modalRef}
        onMouseDown={(e) => e.stopPropagation()}
        className="relative z-[1] w-full sm:max-w-5xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
      >
        {/* ── Header ── */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="14" rx="2"/>
                <circle cx="9" cy="8" r="2.2"/>
                <path d="M21 14l-4.5-4.5L9 15l-2-2-4 4"/>
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">בחירת תמונה לקורס</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">בחר/י תמונה מתאימה עבור {courseTitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="סגירה"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Body: sidebar + images ── */}
        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">

          {/* Sidebar — horizontal scroll strip on mobile, vertical list on sm+ */}
          <div className="flex-shrink-0 sm:w-52 border-b sm:border-b-0 sm:border-l border-gray-100 bg-gray-50/60">
            {/* Mobile: horizontal pill tabs */}
            <div className="sm:hidden flex gap-2 px-4 py-3 overflow-x-auto scrollbar-none">
              {collections.map(cat => {
                const active = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setActiveCategory(cat.key)}
                    className={`flex-shrink-0 h-8 px-3 rounded-full text-xs font-semibold transition-all duration-150
                                ${active
                                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-200"
                                  : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600"}`}
                  >
                    {cat.label}
                    <span className={`mr-1 text-[10px] ${active ? "opacity-75" : "text-gray-400"}`}>({cat.images?.length || 0})</span>
                  </button>
                );
              })}
            </div>

            {/* Desktop: vertical list */}
            <div className="hidden sm:block p-3 space-y-1 overflow-y-auto max-h-full">
              {collections.length === 0 ? (
                <p className="text-xs text-gray-400 text-center mt-8 px-2">אין קטגוריות זמינות</p>
              ) : collections.map(cat => {
                const active = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setActiveCategory(cat.key)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 text-right
                                ${active
                                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-200"
                                  : "text-gray-600 hover:bg-white hover:text-indigo-600 hover:shadow-sm"}`}
                  >
                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                      {cat.images?.length || 0}
                    </span>
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Images panel */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-white overflow-hidden">

            {/* Selected preview strip */}
            {selectedImage ? (
              <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-5 py-3 bg-indigo-50/60 border-b border-indigo-100">
                <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-white shadow-sm flex-shrink-0">
                  <img src={selectedImage} alt="נבחרה" className="w-full h-full object-cover"/>
                </div>
                <div className="text-right min-w-0">
                  <p className="text-xs font-bold text-indigo-700">תמונה נבחרת</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">לחץ על תמונה אחרת להחלפה</p>
                </div>
              </div>
            ) : (
              <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-5 py-2.5 border-b border-gray-100">
                <span className="text-xs text-gray-400">
                  <span className="font-semibold text-gray-600">{activeImages.length}</span> תמונות ·{" "}
                  <span className="font-semibold text-gray-600">{pageImages.length}</span> מוצגות
                </span>
                <span className="text-xs text-gray-400">לא נבחרה תמונה</span>
              </div>
            )}

            {/* Grid */}
            {!activeImages.length ? (
              <div className="flex-1 flex items-center justify-center text-gray-300">
                <div className="text-center">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mx-auto mb-2">
                    <rect x="3" y="3" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="9" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M21 14l-4.5-4.5L9 15l-2-2-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <p className="text-sm">אין תמונות בקטגוריה זו</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {pageImages.map((url, i) => {
                      const isSelected = url === selectedImage;
                      const isHovered  = url === hoveredImage;
                      return (
                        <button
                          key={`${url}-${i}`}
                          type="button"
                          onClick={() => setSelectedImage(url)}
                          onMouseEnter={() => setHoveredImage(url)}
                          onMouseLeave={() => setHoveredImage(null)}
                          className={`relative group rounded-2xl overflow-hidden aspect-[4/3] transition-all duration-200
                                      ${isSelected
                                        ? "ring-[3px] ring-indigo-500 ring-offset-2 shadow-lg"
                                        : "shadow-sm hover:shadow-lg hover:scale-[1.03]"}`}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover"/>

                          {/* hover overlay */}
                          <div className={`absolute inset-0 bg-black/30 transition-opacity duration-150
                                          ${isSelected ? "opacity-0" : "opacity-0 group-hover:opacity-100"}`}/>

                          {/* selected check */}
                          {isSelected && (
                            <div className="absolute inset-0 flex items-center justify-center bg-indigo-600/20">
                              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md">
                                <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              </div>
                            </div>
                          )}

                          {/* caption */}
                          <div className={`absolute bottom-0 inset-x-0 py-1 text-center text-[10px] font-semibold text-white transition-all duration-150
                                          ${isSelected
                                            ? "bg-indigo-600/80 opacity-100 translate-y-0"
                                            : "bg-black/60 opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0"}`}>
                            {isSelected ? "נבחרה ✓" : "בחר"}
                          </div>
                        </button>
                      );
                    })}

                    {/* Ghost fill cells */}
                    {Array.from({ length: Math.max(0, PAGE_SIZE - pageImages.length) }).map((_, i) => (
                      <div key={`empty-${i}`} className="rounded-2xl border-2 border-dashed border-gray-100 aspect-[4/3]"/>
                    ))}
                  </div>
                </div>

                {/* Pagination bar */}
                {totalPages > 1 && (
                  <div className="flex-shrink-0 flex items-center justify-center gap-1.5 px-4 py-3 border-t border-gray-100 bg-white/80 backdrop-blur-sm">
                    {/* progress */}
                    <span className="text-[11px] text-gray-400 font-medium ml-2 tabular-nums">{page}/{totalPages}</span>

                    <button type="button" onClick={goPrev} disabled={page<=1} aria-label="עמוד קודם" className={pageBtnCls(page<=1)}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>

                    {pageNumbers.map(item =>
                      item.type === "ellipsis" ? (
                        <span key={item.key} className="w-8 text-center text-xs text-gray-400 select-none">…</span>
                      ) : (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => setPage(item.value)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold border transition-all duration-150 active:scale-95
                                      ${item.value===page
                                        ? "bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-200"
                                        : "bg-white text-gray-500 border-gray-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-300 shadow-sm"}`}
                        >
                          {item.value}
                        </button>
                      )
                    )}

                    <button type="button" onClick={goNext} disabled={page>=totalPages} aria-label="עמוד הבא" className={pageBtnCls(page>=totalPages)}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7"/>
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400 hidden sm:block">
            ניתן לשנות את התמונה בכל רגע מ<span className="font-semibold text-gray-600">הקורסים שלי</span>.
          </p>
          <div className="flex items-center gap-3 mr-auto">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                         hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!selectedImage}
              className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                         shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                         disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                         transition-all duration-150 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              שמור תמונה
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CourseImagePickerModal;